// contexts/StoreContext.js
import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import { api } from "../hooks/apiClient";
import { AuthContext } from "./AuthContext";

const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
  const readSaved = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch {
      return null;
    }
  };
  const [selectedStore, setSelectedStore] = useState(() =>
    readSaved("selectedStore"),
  );
  const [selectedDestStore, setSelectedDestStore] = useState(() =>
    readSaved("selectedDestStore"),
  );
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState(null);

  useEffect(() => {
    if (selectedStore)
      localStorage.setItem("selectedStore", JSON.stringify(selectedStore));
    else localStorage.removeItem("selectedStore");
  }, [selectedStore]);

  useEffect(() => {
    if (selectedDestStore)
      localStorage.setItem(
        "selectedDestStore",
        JSON.stringify(selectedDestStore),
      );
    else localStorage.removeItem("selectedDestStore");
  }, [selectedDestStore]);
  const clearSelectedStore = useCallback(() => setSelectedStore(null), []);
  const clearSelectedDestStore = useCallback(
    () => setSelectedDestStore(null),
    [],
  );

  const { user } = useContext(AuthContext);

  const loadStores = useCallback(async () => {
    setStoresLoading(true);
    setStoresError(null);
    try {
      const res = await api.get("stores");
      const list = res.data?.data ?? [];
      setStores(list);
      setSelectedStore(
        (cur) =>
          list.find((s) => s.id === cur?.id) ??
          (list.length === 1 ? list[0] : null),
      );
    } catch (err) {
      setStoresError(err); // keep the saved selection on network errors
      console.error(err);
    } finally {
      setStoresLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const contextValue = useMemo(
    () => ({
      selectedStore,
      selectedDestStore,
      stores,
      storesLoading,
      storesError,
      reloadStores: loadStores,
      updateSelectedStore: setSelectedStore,
      updateSelectedDestStore: setSelectedDestStore,
      clearSelectedStore,
      clearSelectedDestStore,
      isStoreSelected: !!selectedStore,
      isDestStoreSelected: !!selectedDestStore,
      isStoreActive: selectedStore?.isActive === true, // adjust to your real field
    }),
    [
      selectedStore,
      selectedDestStore,
      stores,
      storesLoading,
      storesError,
      loadStores,
      clearSelectedStore,
      clearSelectedDestStore,
    ],
  );

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};
